import axios from "axios";
import { getApiBaseUrl } from "./env";
import {
  clearAuthState,
  getRefreshToken,
  getCsrfTokenFromCookie,
  setAuthTokensForStorage,
  useRefreshCookie,
} from "./authStorage";
import { refreshTokenSchema } from "@io-spoke/contracts/auth";

type RefreshResponse = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
};

export async function refreshSession() {
  const { value: refreshToken, storage } = getRefreshToken();
  const useCookies = useRefreshCookie();
  if ((!refreshToken || !storage) && !useCookies) {
    clearAuthState();
    throw new Error("Refresh token is missing.");
  }

  const api = axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: useCookies,
  });

  const csrfToken = useCookies ? getCsrfTokenFromCookie() : null;
  const payload = refreshToken ? { refreshToken } : {};
  const parsed = refreshTokenSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? "Invalid refresh payload.");
  }
  const response = await api.post<RefreshResponse>("/auth/refresh", parsed.data, {
    headers: csrfToken ? { "x-csrf-token": csrfToken } : undefined,
  });
  const { accessToken, refreshToken: newRefreshToken, idToken } = response.data;

  if (!accessToken) {
    throw new Error("Auth API did not return an access token.");
  }

  if (storage) {
    setAuthTokensForStorage(storage, { accessToken, refreshToken: newRefreshToken ?? refreshToken, idToken });
  } else {
    setAuthTokensForStorage(sessionStorage, { accessToken, idToken });
  }

  return response.data;
}

export async function logoutSession() {
  const useCookies = useRefreshCookie();
  const csrfToken = useCookies ? getCsrfTokenFromCookie() : null;
  const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: useCookies });
  await api.post("/auth/logout", undefined, {
    headers: csrfToken ? { "x-csrf-token": csrfToken } : undefined,
  });
}
